import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Icon, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { loader } from 'graphql.macro';
import { useState, useMemo, useEffect, useRef } from 'react';
import InspectionTypeBadge from '../../components/inspections/InspectionTypeBadge';
import LabelSelector from '../../components/LabelSelector';
import SimpleUserCard from '../../components/SimpleUserCard';
import { Text } from '../../components/typography';
import UserMultiSelector from '../../components/UserMultiSelector';
import {
  assessmentStatus,
  inspectionApprovalTypes,
  paths,
} from '../../constants/strings';
import { getLocalTime, setPdfTimeSetting } from '../../utilities/time';
import styles from './RightSidebar.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../../graphql/cache/modal';
import { toastVariant, toastLength, reviewStatus } from '../../constants/misc';
import { Button } from '../../components';
import { AssessmentDocument } from '../../utilities/pdf_export/assessment_components/assessment.js';
import { getRoute } from '../../constants/strings';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { useCurrentUser } from '../../providers/UserProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useModal } from '../../providers/ModalProvider.jsx';
import Label from '../../components/action_items/Label.jsx';
import { v4 as uuidv4 } from 'uuid';
import { pdf } from '@react-pdf/renderer';
import { CF_DOMAIN } from '../../constants/aws.js';
import axios from 'axios';
import {
  downloadFile,
  formatBundleItems,
  getCategoryConditionalTriggers,
  isCategoryVisible,
} from '../../utilities/inspection.js';
import ReportSelector from '../../utilities/pdf_export/ReportSelector.jsx';
import IncidentSearch from '../incidents/IncidentSearch.jsx';
import { useInspection } from '../../hooks/offline-hooks/createInspectionhook.js';
import useCheckPermission from '../../providers/UsePermissionHook.jsx';
import { actions, customColors, navigationRoutes } from '../../utilities';
import TextWithIcon from '../../components/typography/TextWithIcon.jsx';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider.jsx';
import { useWorkspaceMutations } from '../../hooks/addUserToWorkspace.js';
import { downloadPDF } from '../../utilities/pdf_export/misc.js';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PrivateSharedSelectors from '../../components/PrivateSharedSelectors.jsx';
import SimpleWorkspaceCard from '../../components/SimpleWorkspaceCard.jsx';
import { Form } from 'react-bootstrap';
import { DropdownText } from '../../components/dropdowns/DropdownText.jsx';
import Dropdown from '../../components/dropdownV2/Dropdown.jsx';
import { useChangeWorkspace, useMobileListener } from '../../hooks/misc.js';

const XLSX = require('xlsx');

const addLabelMutation = loader('./RightSidebar.addLabel.graphql');
const deleteLabelMutation = loader('./RightSidebar.deleteLabel.graphql');
const addReviewerMutation = loader('./RightSidebar.addReviewer.graphql');
const deleteReviewerMutation = loader('./RightSidebar.deleteReviewer.graphql');
const addRegretMutation = loader('./RightSidebar.addRegret.graphql');
const deleteRegretMutation = loader('./RightSidebar.deleteRegret.graphql');
const addParticipantMutation = loader('./RightSidebar.addParticipant.graphql');
const deleteParticipantMutation = loader(
  './RightSidebar.deleteParticipant.graphql',
);
const deleteInspectionMutation = loader(
  './RightSidebar.deleteInspection.graphql',
);
const revertInspectionMutation = loader('./Inspection.revert.graphql');
const uploadUrlQuery = loader('../../graphql/queries/s3.graphql');
const saveVersionMutation = loader('./RightSidebar.saveState.graphql');
const updateActionItemMutation = loader('./RightSidebar.transfer.graphql');
const copyInspectionAnswersMutation = loader(
  './RightSidebar.copyAnswers.graphql',
);

const revisionsQuery = loader('./Inspection.originalTemplate.graphql');
const deleteIncidentLinkMutation = loader(
  './RightSidebar.deleteIncidentLink.graphql',
);

const regretsQuery = loader('./RightSidebar.regrets.graphql');
const riskRegistryQuery = loader(
  '../risk_registry/RiskRegistrySelector.existing.graphql',
);

const downloadSignatureSheet = async (signatures, inspection) => {
  const {
    template: { title },
    subject,
  } = inspection;
  const workbook = XLSX.utils.book_new();
  const headers = ['signed_name', 'first_name', 'last_name', 'url'];
  const worksheet = XLSX.utils.json_to_sheet([], { header: headers });
  signatures?.forEach((s) => {
    const rowData = [s.name, s.creator.firstName, s.creator.lastName, s.url];
    XLSX.utils.sheet_add_aoa(worksheet, [rowData], { origin: -1 });
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, 'SIGNATURES');
  XLSX.writeFile(
    workbook,
    `${title}-${subject ? `${subject}-` : ''}signatures.xlsx`,
  );
};

export default function RightSidebar({
  inspection,
  refetch,
  className,
  onClose,
  notes,
  correctiveActions,
  actionItems,
  updateInspection,
  focusReviewers = false,
  onReviewersFocused = () => {},
}) {
  const { disableAssessmentSignature, disableIncidents, enableHazardLog } =
    useFlags();
  const isOnline = useOnlineStatus();
  const client = useApolloClient();
  const {
    workspace,
    isWorkspaceAdmin,
    workspaceId,
    companyPermissions,
    refetchWorkspacePermission,
    allWorkspaces,
    availableWorkspaces,
  } = useWorkspace() || {};
  const { addUser } = useWorkspaceMutations();
  const { companyAdmins } = useWorkspacePermissions();
  const { user, isAdmin, assessmentName, incidentName } = useCurrentUser();
  const isMilitary = user?.timeSetting === 'military';
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const { changeWorkspace } = useChangeWorkspace();
  const [addLabel] = useMutation(addLabelMutation);
  const [deleteLabel] = useMutation(deleteLabelMutation);
  const [addParticipant] = useMutation(addParticipantMutation);
  const [deleteParticipant] = useMutation(deleteParticipantMutation);
  const [deleteReviewer] = useMutation(deleteReviewerMutation);
  const [deleteInspection] = useMutation(deleteInspectionMutation);
  const [revertInspection] = useMutation(revertInspectionMutation);
  const [copyInspectionAnswers] = useMutation(copyInspectionAnswersMutation);
  const [deleteIncidentLink] = useMutation(deleteIncidentLinkMutation);
  const [closeLabels, setCloseLabels] = useState(false);
  const { t } = useTranslation();
  const [expandList, setExpandList] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openConfirmationModal } = useModal();
  const { addInspection } = useInspection();
  const [updateActionItem] = useMutation(updateActionItemMutation);
  const [saveAssessmentVersion] = useMutation(saveVersionMutation);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [location, setLocation] = useState(inspection?.location);
  const { state: browserState = {} } = useLocation();
  const { listIds = [], index: rowIndex = 0 } = browserState || {};

  const isMobile = useMobileListener();

  const { data: { inspectionTemplates: fetchedTemplate = [{}] } = {} } =
    useQuery(revisionsQuery, {
      skip: !online || !inspection?.template?.id,
      variables: {
        options: {
          filters: [
            {
              field: 'id',
              operator: 'eq',
              value: [inspection?.template?.id.toString()],
            },
          ],
        },
      },
    });

  const { refetch: refetchRegrets, data: { regrets = [] } = {} } = useQuery(
    regretsQuery,
    {
      skip: !inspection?.id || !online,
      variables: {
        inspectionId: `${inspection?.id}`,
      },
    },
  );

  const { data: { riskRegistry: associatedRisks = [] } = {} } = useQuery(
    riskRegistryQuery,
    {
      skip: !enableHazardLog || !inspection?.id,
      variables: {
        modelId: `${inspection?.id}`,
        association: 'inspections',
      },
    },
  );

  const [addReviewer] = useMutation(addReviewerMutation, {
    update(cache, { data: { addInspectionReviewer: newReviewer } }) {
      cache.modify({
        id: cache.identify(inspection),
        fields: {
          reviewers(existingReviewers = []) {
            const newReviewerRef = cache.identify(newReviewer);
            return [...existingReviewers, { __ref: newReviewerRef }];
          },
        },
      });
    },
  });

  const [addRegret] = useMutation(addRegretMutation);
  const [deleteRegret] = useMutation(deleteRegretMutation);
  const [editingParticipants, setEditingParticipants] = useState(false);
  const [editingReviewers, setEditingReviewers] = useState(false);
  const [highlightReviewers, setHighlightReviewers] = useState(false);
  const reviewersRef = useRef(null);

  useEffect(() => {
    if (!focusReviewers) return;
    if (inspection?.isDraft && online) setEditingReviewers(true);
    setHighlightReviewers(true);
    requestAnimationFrame(() =>
      reviewersRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      }),
    );
    onReviewersFocused();
  }, [focusReviewers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!highlightReviewers) return;
    const timeout = setTimeout(() => setHighlightReviewers(false), 2000);
    return () => clearTimeout(timeout);
  }, [highlightReviewers]);
  const [editingWorkspace, setEditingWorkspace] = useState(false);
  const [editingRegrets, setEditingRegrets] = useState(false);
  const [editingIncidents, setEditingIncidents] = useState(false);
  const [isPrivate, setIsPrivate] = useState(!!inspection.private);
  const [copyAnswers, setCopyAnswers] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    inspection?.workspace || null,
  );
  const [showMoreUsers, setShowMoreUsers] = useState(null);

  const inspectionReportOptions = useMemo(
    () =>
      [
        { id: 'actions', title: t(keys.assessments.REPORT_ACTION_ITEMS) },
        { id: 'notes', title: t(keys.assessments.REPORT_NOTES) },
        { id: 'signatures', title: t(keys.assessments.REPORT_SIGNATURES) },
        { id: 'files', title: t(keys.assessments.REPORT_REFERENCE_FILES) },
        { id: 'labels', title: t(keys.assessments.REPORT_LABELS) },
        inspection?.reviewers?.length && {
          id: 'reviews',
          title: t(keys.assessments.REPORT_REVIEWS),
        },
      ].filter(Boolean),
    [t, inspection?.reviewers],
  );

  const userSelectorStatus = useMemo(() => {
    return (!!selectedWorkspace?.value && !!selectedWorkspace?.id) ||
      !!inspection?.workspace?.id
      ? 'workspace'
      : 'all';
  }, [inspection, selectedWorkspace]);

  const participantAccess = useMemo(() => {
    const allWorkspaceUsers = companyPermissions
      ?.filter((c) => c.workspaceId === inspection?.workspace?.id)
      ?.map((p) => p.user);
    const workspaceUsers = [...companyAdmins, ...allWorkspaceUsers];
    return new Set(workspaceUsers.map((user) => user.id));
  }, [companyPermissions, companyAdmins, inspection?.workspace?.id]);

  const approvalIds = useMemo(() => {
    if (!inspection?.approvals?.length) {
      return [];
    }
    const approvedUserIds = inspection?.approvals
      ?.filter((a) => a.status === inspectionApprovalTypes.approved)
      .map((a) => a.creator.id);

    return approvedUserIds;
  }, [inspection?.approvals]);

  const isCreator = user?.id === inspection?.creator?.id;
  const isAudit = inspection.inspectionType === 'AUDIT';

  const generateFileBlob = async (document) => {
    setPdfTimeSetting(user?.timeSetting);
    return pdf(document).toBlob();
  };

  const handleFileUpload = async (file) => {
    const fileName = uuidv4();
    const {
      data: { simpleStorageUploadUrl: url },
    } = await client.query({
      query: uploadUrlQuery,
      variables: {
        fileName: `assets/${user?.company?.id}/${fileName}.pdf`,
        fileType: 'pdf',
      },
    });
    const options = {
      headers: {
        'Content-Type': 'application/pdf',
      },
    };

    const result = await axios.put(url, file, options);

    if (result.status !== 200) return '';
    return `${CF_DOMAIN(user)}${fileName}.pdf`;
  };

  const AssessmentCard = ({ title, editedOn, version, onClick }) => (
    <div onClick={onClick} className={styles.cardContainer}>
      <div>
        <Text noMargin weight="semiBold">
          {title}
        </Text>
        <Text noMargin size="sm">
          {t(keys.common.EDITED_ON, {
            variable: getLocalTime(editedOn).format(
              isMilitary ? 'ddd, MMM D YYYY, HH:mm' : 'ddd, MMM D YYYY, h:mma',
            ),
          })}
        </Text>
      </div>
      <div className={styles.downloadContainer}>
        <Text noMargin weight="semiBold" size="sm" color="secondary">
          V{version}
        </Text>
        <Icon
          baseClassName="material-icons-outlined"
          className={styles.downloadIcon}
        >
          download
        </Icon>
      </div>
    </div>
  );
  const hasReviewPermission = useCheckPermission({
    action: actions.review,
    model: 'incident',
  });

  const openActionItems = useMemo(() => {
    return (
      actionItems?.filter(
        (a) => a.status !== 'DONE' && a.status !== 'DUPLICATE',
      ) || []
    );
  }, [actionItems]);

  const { itemMap } = inspection?.items?.length
    ? formatBundleItems(inspection.items)
    : { itemMap: {} };
  const categoryConditionalTriggers = getCategoryConditionalTriggers(
    inspection?.template?.categories,
  );

  const contentOptions = [
    ...(inspection?.template?.categories
      ?.filter((c) =>
        isCategoryVisible(c, categoryConditionalTriggers, itemMap),
      )
      ?.map((c) => ({
        id: c.id,
        title: c.title,
      })) || []),
  ];

  const reportedWorkspace = allWorkspaces?.find(
    (w) => w.id === inspection.reportedWorkspaceId,
  );

  const { signatures, sigCount, participantSigCount, activeParticipantCount } =
    useMemo(() => {
      const signatures = inspection?.attachments?.filter(
        (a) => a?.extension === 'SIGNATURE',
      );
      const signedIds = new Set(signatures.map((sig) => sig.creator?.id));

      const count = regrets.filter((regret) => signedIds.has(regret.id)).length;
      const activeParticipants =
        inspection?.participants?.filter((p) => p.active) ?? [];
      return {
        signatures,
        sigCount: count,
        participantSigCount: activeParticipants.filter((p) =>
          signedIds.has(p.id),
        ).length,
        activeParticipantCount: activeParticipants.length,
      };
    }, [inspection, regrets]);

  const participantSignatureRequired =
    !!inspection?.template?.templateOptions?.participantSignatureRequired &&
    !inspection?.template?.templateOptions?.disableSignatureBox &&
    !disableAssessmentSignature;

  const { displayRegrets, allRegrets } = useMemo(() => {
    return {
      displayRegrets: [
        ...(showMoreUsers === 'regrets' ? regrets : regrets?.slice(0, 5) ?? []),
      ]?.sort((a, b) => (a?.firstName || '').localeCompare(b?.firstName || '')),
      allRegrets: regrets ?? [],
    };
  }, [showMoreUsers, regrets]);

  const { displayParticipants, allParticipants } = useMemo(() => {
    return {
      displayParticipants: [
        ...(showMoreUsers === 'participants'
          ? inspection?.participants
          : inspection?.participants?.slice(0, 5) ?? []),
      ]?.sort((a, b) => (a?.firstName || '').localeCompare(b?.firstName || '')),
      allParticipants: inspection?.participants ?? [],
    };
  }, [inspection, showMoreUsers]);

  const hideCompleter = useMemo(
    () => inspection?.inspectionType === 'EXTERNAL',
    [inspection?.inspectionType],
  );

  return !inspection?.id ? (
    <></>
  ) : (
    <div
      style={{
        overflow: 'scroll',
      }}
    >
      <div
        className={classNames([styles.container, className])}
        data-cy={'right-sidebar'}
      >
        <div className={styles.header}>
          <Text size="lg" weight="bold">
            {t(keys.common.INFORMATION)}
          </Text>
          {isMobile ? (
            <div
              onClick={() => {
                setCloseLabels(true);
                onClose();
              }}
              className={styles.closeIcon}
            >
              <Icon>close</Icon>
            </div>
          ) : (
            online &&
            !!listIds?.length && (
              <div className={styles.navigate}>
                <Button
                  size="xs"
                  outlined
                  disabled={rowIndex <= 0 || !listIds?.length}
                  icon="arrow_back_ios"
                  value={t(keys.action.BACK)}
                  onClick={() => {
                    const previousInspectionId = listIds[rowIndex - 1];
                    navigate(
                      getRoute(
                        workspaceId,
                        paths.assessment,
                        previousInspectionId,
                      ),
                      { state: { ...browserState, index: rowIndex - 1 } },
                    );
                  }}
                />
                <Button
                  size="xs"
                  disabled={
                    rowIndex + 1 === listIds?.length || !listIds?.length
                  }
                  outlined
                  rightIcon="arrow_forward_ios"
                  value={t(keys.action.NEXT)}
                  onClick={() => {
                    const nextInspectionId = listIds[rowIndex + 1];
                    navigate(
                      getRoute(workspaceId, paths.assessment, nextInspectionId),
                      { state: { ...browserState, index: rowIndex + 1 } },
                    );
                  }}
                />
              </div>
            )
          )}
        </div>
        <InspectionTypeBadge type={inspection.inspectionType} />
        {!!inspection?.initialInspection?.length > 0 && (
          <div>
            <div className={styles.sectionLine} />
            <TextWithIcon
              link
              hover
              weight="semiBold"
              color="secondary"
              noMargin
              onClick={() =>
                navigate(
                  getRoute(
                    inspection?.workspaceId,
                    paths.assessment,
                    inspection?.initialInspection[0]?.id,
                  ),
                )
              }
            >
              Initial Assessment
            </TextWithIcon>
          </div>
        )}
        <div className={styles.sectionLine} />
        {!isAudit && (
          <>
            <PrivateSharedSelectors
              workspaceTitle={reportedWorkspace?.title || workspace?.title}
              isPrivate={isPrivate}
              onPrivateSubmit={() => {
                updateInspection({
                  variables: {
                    id: inspection?.id,
                    private: !isPrivate,
                  },
                }).then(() => setIsPrivate(!isPrivate));
              }}
            />
            <div className={styles.sectionLine} />
          </>
        )}
        <Text
          className={styles.heading}
          noMargin
          size="md"
          weight="bold"
          color="secondary"
        >
          {t(keys.common.STATUS)}
        </Text>
        <div className={styles.statusLabel}>
          <Label
            rowSized={false}
            className={styles.label}
            name={t(assessmentStatus[inspection.status]?.title)}
            color={assessmentStatus[inspection.status]?.color}
          />
        </div>
        <div className={styles.sectionLine} />
        {!disableIncidents && (
          <div>
            <div className={styles.editable}>
              <Text
                className={styles.heading}
                noMargin
                size="md"
                weight="bold"
                color="secondary"
              >
                {t(keys.assessments.RELATED_INCIDENTS)}
              </Text>
              {hasReviewPermission && (
                <Icon
                  onClick={() => setEditingIncidents((prev) => !prev)}
                  className={styles.icon}
                  baseClassName="material-icons-outlined"
                >
                  {`${editingIncidents ? 'close' : 'settings'}`}
                </Icon>
              )}
            </div>
            {editingIncidents && (
              <IncidentSearch
                hideTitle
                inspectionId={inspection?.id}
                onSubmit={(incident) => {
                  if (inspection?.inspectionType === 'INCIDENT') {
                    const participants = incident.participants || [];
                    for (let user of participants) {
                      addParticipant({
                        variables: {
                          userId: user.id,
                          inspectionId: inspection?.id,
                        },
                      });
                    }
                  }
                  refetch();
                }}
                skip={!!inspection?.incident?.id}
              />
            )}
            {editingIncidents && <div className={styles.spacer} />}
            {inspection?.incidents?.length > 0 ? (
              inspection?.incidents?.map((incident) => (
                <div className={styles.flexRow}>
                  <TextWithIcon
                    noMargin
                    color="accentPrimary"
                    hover
                    onClick={() =>
                      navigate(
                        `/${paths.incident}/${incident.id}?section=REPORTS`,
                      )
                    }
                    weight="semiBold"
                    link
                  >
                    {incident?.externalId ||
                      incident?.title ||
                      t(keys.common.INCIDENT)}
                  </TextWithIcon>
                  {editingIncidents && (
                    <Icon
                      onClick={() =>
                        openConfirmationModal({
                          title: t(keys.incidents.BREAK_LINK_HEADER, {
                            variable:
                              assessmentName || t(keys.common.ASSESSMENT),
                            incidentName:
                              incidentName || t(keys.common.INCIDENT),
                          }),
                          variant: 'warning',
                          description: t(
                            keys.incidents.BREAK_LINK_DESCRIPTION,
                            {
                              variable: inspection?.template?.title,
                              incidentTitle:
                                incident?.externalId ||
                                incident?.title ||
                                incidentName ||
                                t(keys.common.INCIDENT),
                            },
                          ),
                          onSubmit: () => {
                            deleteIncidentLink({
                              variables: {
                                inspectionId: inspection.id,
                                incidentId: incident?.id,
                              },
                            }).then(() => refetch());
                          },
                        })
                      }
                      baseClassName="material-icons-outlined"
                      className={styles.removeIcon}
                    >
                      remove
                    </Icon>
                  )}
                </div>
              ))
            ) : (
              <Text noMargin color="secondary">
                {t(keys.common.NONE)}
              </Text>
            )}
            <div className={styles.sectionLine} />
          </div>
        )}
        {enableHazardLog && (
          <div>
            <div className={styles.editable}>
              <Text
                className={styles.heading}
                noMargin
                size="md"
                weight="bold"
                color="secondary"
              >
                {t(keys.common.RISK_REGISTRY)}
              </Text>
            </div>
            {associatedRisks?.length > 0 ? (
              associatedRisks.map((risk) => (
                <Text
                  key={risk.id}
                  marginSm
                  truncate
                  weight="semiBold"
                  color="accentPrimary"
                  hover
                  onClick={() =>
                    navigate(`/${paths.registry}/${risk.id}?section=DETAILS`)
                  }
                >
                  {risk.category || `Risk: ${risk.id}`}
                </Text>
              ))
            ) : (
              <Text noMargin color="secondary">
                {t(keys.common.NONE)}
              </Text>
            )}
            <div className={styles.sectionLine} />
          </div>
        )}
        <div>
          <div className={styles.editable}>
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              {t(keys.common.WORKSPACE)}
            </Text>
            <Icon
              onClick={() => setEditingWorkspace(!editingWorkspace)}
              className={styles.icon}
              baseClassName="material-icons-outlined"
            >
              {`${editingWorkspace ? 'close' : 'settings'}`}
            </Icon>
          </div>
          {editingWorkspace ? (
            <Dropdown
              selected={selectedWorkspace}
              options={availableWorkspaces}
              onChange={async (w) => {
                setSelectedWorkspace(w || null);
                if (!!w?.id || !!w?.value) {
                  changeWorkspace(w.id || w.value);
                }
                const workspaceId = w?.id || w?.value || null;
                await updateInspection({
                  variables: {
                    id: inspection.id,
                    workspaceId,
                  },
                }).then(() => {
                  setEditingWorkspace(false);
                  refetch();
                });
              }}
              type="workspace"
            />
          ) : (
            <SimpleWorkspaceCard workspace={selectedWorkspace} />
          )}
        </div>
        {inspection?.template?.reviewRequired && (
          <div
            ref={reviewersRef}
            className={classNames(highlightReviewers && styles.highlight)}
          >
            <div className={styles.sectionLine} />
            <div className={styles.editable}>
              <Text
                className={styles.heading}
                noMargin
                size="md"
                weight="bold"
                color="secondary"
              >
                {t(keys.common.REVIEWERS)}
              </Text>
              {inspection?.isDraft && online && (
                <Icon
                  onClick={() => setEditingReviewers(!editingReviewers)}
                  className={styles.icon}
                  baseClassName="material-icons-outlined"
                >
                  {`${editingReviewers ? 'close' : 'settings'}`}
                </Icon>
              )}
            </div>
            {editingReviewers ? (
              <UserMultiSelector
                type={userSelectorStatus}
                disableSelectAll
                selected={inspection?.reviewers.map((r) => r.user)}
                onUserAdded={(user) =>
                  addReviewer({
                    variables: {
                      userId: user.id,
                      inspectionId: inspection.id,
                    },
                  })
                }
                onUserRemoved={(user) => {
                  const reviewer = inspection.reviewers.find(
                    (r) => r.user.id === user.id,
                  );

                  if (!reviewer) {
                    throw new Error('Reviewer not found');
                  }

                  deleteReviewer({
                    variables: {
                      reviewerId: reviewer.id,
                    },
                  }).then(() => {
                    client.cache.evict(client.cache.identify(reviewer));
                    client.cache.gc();
                  });
                }}
              />
            ) : inspection?.reviewers?.length ? (
              <div className={styles.participantContainer}>
                {inspection?.reviewers?.map((reviewer) => (
                  <div className={styles.userContainer} key={reviewer.user.id}>
                    <SimpleUserCard
                      size="md"
                      mediumAvatar
                      user={reviewer.user}
                    />
                    {reviewer.status === reviewStatus.approved && (
                      <Icon
                        fontSize="inherit"
                        baseClassName="material-icons-outlined"
                        className={styles.approveIcon}
                      >
                        done
                      </Icon>
                    )}
                    {reviewer.status === reviewStatus.changeRequest && (
                      <Icon
                        fontSize="inherit"
                        baseClassName="material-icons-outlined"
                        className={styles.changeRequestIcon}
                      >
                        warning
                      </Icon>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Text noMargin color="secondary">
                {t(keys.common.NONE)}
              </Text>
            )}
          </div>
        )}
        {!!inspection.location && (
          <>
            <div className={styles.sectionLine} />
            <div className={styles.editable}>
              <Text
                className={styles.heading}
                noMargin
                size="md"
                weight="bold"
                color="secondary"
                icon={inspection.isDraft ? 'edit' : null}
              >
                {t(keys.common.LOCATION)}
              </Text>
              <Icon
                baseClassName="material-icons-outlined"
                style={{ fontSize: '1.2rem', opacity: 0.8 }}
                onClick={() => {
                  setIsEditingLocation(!isEditingLocation);
                  updateInspection({
                    variables: { id: inspection.id, location },
                  });
                }}
              >
                {isEditingLocation ? 'check' : 'edit'}
              </Icon>
            </div>
            {isEditingLocation && inspection.isDraft ? (
              <div>
                {!!workspace?.locations ? (
                  <DropdownText
                    placeholder={'Location'}
                    selected={location}
                    items={workspace.locations.split('|')}
                    onChange={(location) => setLocation(location)}
                    onBlur={() => {
                      updateInspection({
                        variables: {
                          id: inspection?.id,
                          location,
                        },
                      });
                    }}
                    required
                  />
                ) : (
                  <div>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      onBlur={() => {
                        updateInspection({
                          variables: {
                            id: inspection?.id,
                            location,
                          },
                        });
                      }}
                    />
                    <div className={styles.spacer} />
                  </div>
                )}
              </div>
            ) : (
              <Text weight="semiBold" noMargin>
                {inspection.location}
              </Text>
            )}
          </>
        )}
        {inspection.auditType && isAudit && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              Audit Type
            </Text>
            <Text weight="semiBold" noMargin>
              {inspection.auditType}
            </Text>
          </>
        )}
        {inspection?.creator?.id && (
          <div>
            <div className={styles.sectionLine} />
            <div className={styles.editable}>
              <div className={classNames(styles.heading, styles.flexRow)}>
                <Text noMargin size="md" weight="bold" color="secondary">
                  {t(keys.common.PARTICIPANTS)}
                </Text>
                {participantSignatureRequired && activeParticipantCount ? (
                  <Text noMargin color="secondary" size="sm">
                    {participantSigCount}/{activeParticipantCount}
                  </Text>
                ) : null}
              </div>
              {inspection?.isDraft && online && (
                <Icon
                  onClick={() => setEditingParticipants(!editingParticipants)}
                  className={styles.icon}
                  baseClassName="material-icons-outlined"
                >
                  {`${editingParticipants ? 'close' : 'settings'}`}
                </Icon>
              )}
            </div>
            {editingParticipants ? (
              <UserMultiSelector
                type={userSelectorStatus}
                setUsers={() => refetch()}
                parentId={inspection?.id}
                parentType="inspection"
                selected={inspection?.participants}
                onUserAdded={(user) =>
                  addParticipant({
                    variables: {
                      userId: user.id,
                      inspectionId: inspection.id,
                    },
                  })
                }
                onUserRemoved={(user) =>
                  deleteParticipant({
                    variables: {
                      userId: user.id,
                      inspectionId: inspection.id,
                    },
                  })
                }
              />
            ) : displayParticipants?.length ? (
              <div className={styles.participantContainer}>
                {displayParticipants?.map((user) => {
                  const isInWorkspace =
                    participantAccess?.has(user.id) || user.role === 'ADMIN';
                  if (!user.active) {
                    return null;
                  }
                  return (
                    <div className={styles.userContainer} key={user.id}>
                      <div className={styles.signatureCard}>
                        <SimpleUserCard size="md" mediumAvatar user={user} />
                        {participantSignatureRequired &&
                          signatures?.find(
                            (s) => s?.creator?.id === user?.id,
                          ) && (
                            <Icon
                              className={styles.green}
                              size="sm"
                              baseClassName="material-icons-outlined"
                            >
                              draw
                            </Icon>
                          )}
                      </div>
                      {approvalIds.includes(user.id) && (
                        <Icon
                          fontSize="inherit"
                          baseClassName="material-icons-outlined"
                          className={styles.approveIcon}
                        >
                          done
                        </Icon>
                      )}
                      {!isInWorkspace && !!inspection?.workspace?.id ? (
                        <Tooltip
                          title={`No Access to ${inspection.workspace?.title} `}
                        >
                          <Icon
                            style={{
                              color: customColors.RED_LIGHT,
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                            }}
                            onClick={() => {
                              if (isAdmin || isWorkspaceAdmin) {
                                openConfirmationModal({
                                  variant: 'warning',
                                  buttonText: 'Add to Workspace',
                                  title: `User Workspace Permission`,
                                  description: `${user.firstName} ${user.lastName} does not have workspace permissions for ${inspection.workspace.title} and will not be able to view this Incident. Would you like to add them?`,
                                  onSubmit: () => {
                                    addUser({
                                      userId: user.id,
                                      workspaceId: inspection.workspace?.id,
                                    }).then(() => refetchWorkspacePermission());
                                  },
                                });
                              }
                            }}
                          >
                            error
                          </Icon>
                        </Tooltip>
                      ) : null}
                    </div>
                  );
                })}
                {allParticipants?.length > 5 && (
                  <Text
                    noMargin
                    color="accentPrimary"
                    hover
                    onClick={() =>
                      setShowMoreUsers((prev) =>
                        prev === 'participants' ? null : 'participants',
                      )
                    }
                  >
                    {showMoreUsers === 'participants'
                      ? 'Show Less'
                      : `Show more (+${allParticipants?.length - 5})`}
                  </Text>
                )}
              </div>
            ) : (
              <Text noMargin color="secondary">
                {t(keys.common.NONE)}
              </Text>
            )}
          </div>
        )}
        {inspection.inspectionType === 'MEETING' ? (
          <div>
            <div className={styles.sectionLine} />
            <div className={styles.editable}>
              <div className={classNames(styles.heaing, styles.flexRow)}>
                <Text noMargin size="sm" weight="bold" color="secondary">
                  {t(keys.common.REGRETS)}
                </Text>
                {signatures?.length > 5 && regrets?.length > 5 ? (
                  <Text noMargin color="secondary" size="sm">
                    {sigCount}/{regrets?.length}
                  </Text>
                ) : null}
              </div>
              {inspection?.isDraft && online && (
                <Icon
                  onClick={() => setEditingRegrets(!editingRegrets)}
                  className={styles.icon}
                  baseClassName="material-icons-outlined"
                >
                  {`${editingRegrets ? 'close' : 'settings'}`}
                </Icon>
              )}
            </div>
            {editingRegrets ? (
              <UserMultiSelector
                parentId={inspection?.id}
                parentType="regret"
                type={userSelectorStatus}
                selected={regrets}
                onUserAdded={(user) =>
                  addRegret({
                    variables: {
                      userId: user.id,
                      inspectionId: inspection.id,
                    },
                  }).then(refetchRegrets)
                }
                onUserRemoved={(user) => {
                  deleteRegret({
                    variables: {
                      userId: user.id,
                      regretableId: inspection?.id,
                    },
                  }).then(refetchRegrets);
                }}
                refetch={refetchRegrets}
              />
            ) : displayRegrets?.length ? (
              <div className={styles.participantContainer}>
                {displayRegrets?.map((user) => (
                  <div
                    className={styles.signatureCard}
                    key={`regrets-list-${user.id}`}
                  >
                    <SimpleUserCard
                      key={`regret-${user.id}`}
                      size="md"
                      mediumAvatar
                      user={user}
                    />
                    {signatures?.find((s) => s?.creator?.id === user?.id) && (
                      <Icon
                        className={styles.green}
                        size="sm"
                        baseClassName="material-icons-outlined"
                      >
                        draw
                      </Icon>
                    )}
                  </div>
                ))}
                {allRegrets?.length > 5 && (
                  <Text
                    noMargin
                    color="accentPrimary"
                    hover
                    onClick={() =>
                      setShowMoreUsers((prev) =>
                        prev === 'regrets' ? null : 'regrets',
                      )
                    }
                  >
                    {showMoreUsers === 'regrets'
                      ? 'Show Less'
                      : `Show more (+${allRegrets?.length - 5})`}
                  </Text>
                )}
              </div>
            ) : (
              <Text noMargin color="secondary">
                {t(keys.common.NONE)}
              </Text>
            )}
          </div>
        ) : null}
        {inspection?.timecard?.id && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              Timecard
            </Text>
            <Text
              className={isAdmin || isCreator ? styles.vehicleLink : null}
              size="md"
              color={isAdmin ? 'accentPrimary' : 'primary'}
              weight="semiBold"
              onClick={() => {
                (isAdmin || isCreator) &&
                  navigate(`/${paths.timecard}/?id=${inspection.timecard.id}`);
              }}
            >
              {inspection.creator.firstName}'s Timecard
            </Text>
          </>
        )}
        <div className={styles.sectionLine} />
        <Text
          className={styles.heading}
          noMargin
          size="md"
          weight="bold"
          color="secondary"
        >
          {t(keys.common.CREATOR)}
        </Text>
        {inspection?.creator ? (
          <SimpleUserCard
            size="md"
            mediumAvatar
            user={inspection.creator}
            smallText={getLocalTime(
              parseInt(inspection.dateCreated, 10),
            ).format(
              isMilitary
                ? 'ddd, MMMM Do YYYY, HH:mm'
                : 'ddd, MMMM Do YYYY, h:mma',
            )}
          />
        ) : (
          <TextWithIcon
            leftIcon="no_accounts"
            iconProps={{ color: customColors.SECONDARY }}
            noMargin
            weight="semiBold"
          >
            Anonymous
          </TextWithIcon>
        )}
        {inspection?.reviewers?.find((r) => r.status !== 'OPEN') && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              {t(keys.common.DATE_REVIEWED)}
            </Text>
            <SimpleUserCard
              size="md"
              mediumAvatar
              user={inspection.reviewers.find((r) => r.status !== 'OPEN')?.user}
              smallText={getLocalTime(
                parseInt(
                  inspection.reviewers.find(
                    (r) =>
                      r.status === 'APPROVED' || r.status === 'CHANGE_REQUEST',
                  )?.dateModified,
                  10,
                ),
              ).format(
                isMilitary
                  ? 'ddd, MMMM Do YYYY, HH:mm'
                  : 'ddd, MMMM Do YYYY, h:mma',
              )}
            />
          </>
        )}
        {!inspection?.isDraft && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              {t(keys.common.DATE_COMPLETED)}
            </Text>
            {hideCompleter ? (
              <Text noMargin color="secondary">
                {getLocalTime(parseInt(inspection.dateCompleted, 10)).format(
                  isMilitary
                    ? 'ddd, MMMM Do YYYY, HH:mm'
                    : 'ddd, MMMM Do YYYY, h:mma',
                )}
              </Text>
            ) : (
              <SimpleUserCard
                size="md"
                mediumAvatar
                user={inspection.completer}
                smallText={getLocalTime(
                  parseInt(inspection.dateCompleted, 10),
                ).format(
                  isMilitary
                    ? 'ddd, MMMM Do YYYY, HH:mm'
                    : 'ddd, MMMM Do YYYY, h:mma',
                )}
              />
            )}
          </>
        )}
        {inspection.subject && isAudit && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              Subject
            </Text>
            <Text weight="semiBold" noMargin>
              {inspection.subject}
            </Text>
          </>
        )}
        {inspection.customer && isAudit && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              Customer
            </Text>
            <Text weight="semiBold" noMargin>
              {inspection.customer}
            </Text>
          </>
        )}
        {online && (
          <>
            <div className={styles.sectionLine} />
            <Text
              className={styles.heading}
              noMargin
              size="md"
              weight="bold"
              color="secondary"
            >
              Labels
            </Text>
            <LabelSelector
              onClose={closeLabels}
              setOnClose={setCloseLabels}
              selected={inspection?.labels || []}
              readOnly={inspection?.isDraft && online ? false : true}
              onSelectionChanged={(op, label) => {
                if (op === 'ADD') {
                  addLabel({
                    variables: {
                      inspectionId: inspection.id,
                      labelId: label.id,
                    },
                  }).then(() => {
                    refetch();
                  });
                } else if (op === 'REMOVE') {
                  deleteLabel({
                    variables: {
                      inspectionId: inspection.id,
                      labelId: label.id,
                    },
                  }).then(() => {
                    refetch();
                  });
                }
              }}
            />
          </>
        )}
        <div className={styles.sectionLine} />
        {inspection?.previousStates?.length > 0 && (
          <>
            <div className={styles.editable}>
              <Text
                className={styles.heading}
                size="md"
                weight="bold"
                color="secondary"
              >
                {t(keys.assessments.HISTORY)}
              </Text>
              <Icon
                onClick={() => setExpandList(!expandList)}
                className={styles.addIcon}
              >
                {expandList ? 'expand_less' : 'expand_more'}
              </Icon>
            </div>

            {expandList && (
              <div className={styles.previousStateList}>
                {inspection.previousStates.map(
                  ({ version, url, dateEdited }, index) => (
                    <AssessmentCard
                      key={`${index}-history-card`}
                      title={inspection?.template?.title}
                      version={version}
                      editedOn={dateEdited}
                      onClick={() => {
                        const fileName = `${getLocalTime(
                          inspection.dateCreated,
                        ).format(
                          'MM-DD-YYYY',
                        )}_${inspection.template?.title?.replace(' ', '_')}_${
                          inspection.id
                        }_version_${version}.pdf`;
                        downloadFile(url, fileName);
                      }}
                    />
                  ),
                )}
              </div>
            )}
            <div className={styles.sectionLine} />
          </>
        )}
        <ReportSelector
          options={inspectionReportOptions}
          contentOptions={contentOptions}
          isAudit={isAudit}
          inspection={{ ...inspection, regrets: regrets ?? [] }}
          notes={notes}
          actionItems={actionItems || []}
          correctiveActions={correctiveActions}
          fileName={`${getLocalTime(inspection.dateCreated).format(
            'MM-DD-YYYY',
          )}_${inspection.template?.title?.replace(' ', '_')}_${inspection.id}`}
          onSubmit={(selected) => {
            downloadPDF({
              inspection: { ...inspection, regrets: regrets ?? [] },
              props: selected,
              notes,
              actionItems,
              timeSetting: user?.timeSetting,
              fileName: `${getLocalTime(inspection.dateCreated).format(
                'MM-DD-YYYY',
              )}_${inspection.template?.title?.replace(' ', '_')}_${
                inspection.id
              }`,
            });
            if (selected?.includes('signature_export')) {
              downloadSignatureSheet(signatures, inspection);
            }
          }}
        />
        <div className={styles.spacer} />
        {online &&
          inspection.inspectionType !== 'INCIDENT' &&
          inspection.inspectionType !== 'AUDIT' && (
            <>
              <ReportSelector
                showCopy
                options={openActionItems}
                buttonText="Copy"
                buttonIcon="content_copy"
                variant="purple"
                showDescription={true}
                title={t(keys.assessments.COPY_ASSESSMENT)}
                subtitle={
                  !!openActionItems?.length
                    ? t(keys.assessments.SELECT_ACTION_ITEMS)
                    : ''
                }
                copy={copyAnswers}
                setCopy={setCopyAnswers}
                isAudit={false}
                onSubmit={async (actionItems) => {
                  const participants =
                    inspection?.participants?.map((p) => p.id) || [];
                  const { template, vehicle, workspace } = inspection || {};
                  let newAssessmentId;
                  let newestTemplateId = template.id;
                  let newestTemplateType =
                    template.templateType || inspection?.inspectionType;
                  if (template.isArchived) {
                    const { originalTemplate, revisions } =
                      fetchedTemplate[0] || {};
                    const allTemplates = !!originalTemplate?.id
                      ? [
                          originalTemplate,
                          ...(originalTemplate?.revisions || []),
                        ]
                      : revisions;
                    const activeTemplates =
                      allTemplates
                        ?.filter((t) => !t?.isDraft && !t?.isArchived)
                        ?.filter(Boolean) || [];

                    if (activeTemplates.length > 0) {
                      newestTemplateId = activeTemplates[0]?.id;
                      newestTemplateType =
                        activeTemplates[0]?.templateType ||
                        inspection?.inspectionType;
                    } else {
                      showToast({
                        variant: 'danger',
                        title: 'Error Copying Assessment',
                        message:
                          'Looks like this template has been archived - Please check with your administrator. If this problem persists, contact us at support@opasmobile.com',
                      });
                      return;
                    }
                  }
                  if (copyAnswers) {
                    try {
                      const {
                        data: {
                          copyInspectionWithValues: {
                            inspection: copiedInspection,
                            unmatchedAnswerCount,
                          },
                        },
                      } = await copyInspectionAnswers({
                        variables: {
                          inspectionId: inspection?.id,
                          templateId: newestTemplateId,
                        },
                      });
                      newAssessmentId = copiedInspection.id;
                      if (actionItems?.length && newAssessmentId) {
                        for (const id of actionItems) {
                          await updateActionItem({
                            variables: {
                              id,
                              inspectionId: newAssessmentId,
                            },
                          });
                        }
                        // drafts get their status set by the completion check on publish
                        if (!inspection?.isDraft) {
                          await updateInspection({
                            variables: {
                              id: inspection?.id,
                              status: 'COMPLETE',
                            },
                          });
                        }
                      }
                      if (unmatchedAnswerCount > 0) {
                        showToast({
                          variant: 'warning',
                          title: 'Assessment Copied',
                          message: `Assessment copied, but ${unmatchedAnswerCount} answer(s) could not be carried over because the related question changed. Contact your administrator if this problem persists.`,
                        });
                      } else {
                        showToast({
                          title: 'Assessment Successfully Copied!',
                        });
                      }
                    } catch (error) {
                      showToast({
                        variant: 'danger',
                        title: 'Error Copying Assessment',
                        message: `Failed to copy assessment: ${error} If this problem persists, contact us at support@opasmobile.com`,
                      });
                      return;
                    }
                  } else {
                    try {
                      const {
                        data: { addInspection: inspectionToCopy },
                      } = await addInspection({
                        templateId: newestTemplateId,
                        vehicleId: vehicle?.id || null,
                        inspectionType: newestTemplateType,
                        participants,
                        workspaceId: workspace?.id || null,
                        privateAssessment: inspection.private || false,
                      });
                      newAssessmentId = inspectionToCopy.id;
                      if (actionItems?.length && newAssessmentId) {
                        for (const id of actionItems) {
                          await updateActionItem({
                            variables: {
                              id,
                              inspectionId: newAssessmentId,
                            },
                          });
                        }
                        // drafts get their status set by the completion check on publish
                        if (!inspection?.isDraft) {
                          await updateInspection({
                            variables: {
                              id: inspection?.id,
                              status: 'COMPLETE',
                            },
                          });
                        }
                      }
                      showToast({
                        title: 'Assessment Successfully Copied!',
                      });
                    } catch (error) {
                      showToast({
                        variant: 'danger',
                        title: 'Error Copying Assessment',
                        message: `Failed to copy assessment: ${error} If this problem persists, contact us at support@opasmobile.com`,
                      });
                      return;
                    }
                  }
                  if (newAssessmentId) {
                    refetch().then(() => {
                      navigate(
                        getRoute(
                          workspaceId,
                          paths.assessment,
                          newAssessmentId,
                        ),
                      );
                    });
                  }
                }}
              />
              <div className={styles.spacer} />
            </>
          )}
        {!inspection?.isDraft && inspection.inspectionType !== 'AUDIT' && (
          <>
            <Button
              value={loading ? t(keys.action.LOADING) : t(keys.action.EDIT)}
              outlined
              variant="warning"
              icon="change_circle"
              className={styles.deleteButton}
              disabled={!isOnline}
              onClick={() => {
                openConfirmationModal({
                  title: t(keys.assessments.REQUEST_ASSESSMENT_CHANGES),
                  description: t(keys.assessments.REQUEST_CHANGE_MESSAGE),
                  buttonText: 'Draft',
                  variant: 'warning',
                  onSubmit: async () => {
                    try {
                      setLoading(true);
                      const fileBlob = await generateFileBlob(
                        <AssessmentDocument
                          inspection={inspection}
                          actionItems={actionItems || []}
                          workspace={inspection?.workspace}
                          notes={notes}
                          props={
                            disableAssessmentSignature ||
                            inspection?.template?.templateOptions
                              ?.disableSignatureBox
                              ? [
                                  'notes',
                                  ...(inspection?.template?.categories?.map(
                                    ({ id }) => id,
                                  ) ?? []),
                                ]
                              : [
                                  'signatures',
                                  'notes',
                                  ...(inspection?.template?.categories?.map(
                                    ({ id }) => id,
                                  ) ?? []),
                                ]
                          }
                        />,
                      );
                      const s3Url = await handleFileUpload(fileBlob);
                      await saveAssessmentVersion({
                        variables: {
                          inspectionId: inspection.id,
                          url: s3Url,
                        },
                      });

                      await revertInspection({
                        variables: { id: inspection.id },
                      });
                    } catch (error) {
                      console.error('An error occurred:', error);
                      showToast({
                        title: 'Error',
                        message:
                          'There was an error during the backup process.',
                      });
                    } finally {
                      setLoading(false);
                    }
                  },
                });
              }}
            />
            <div className={styles.spacer} />
          </>
        )}
        {!!inspection.isDraft &&
          inspection?.incident?.status !== 'COMPLETE' &&
          online && (
            <div>
              <Button
                className={styles.deleteButton}
                variant="danger"
                outlined
                disabled={
                  !isAdmin &&
                  inspection?.creator?.id !== user.id &&
                  !isWorkspaceAdmin
                }
                icon="delete"
                value={t(keys.action.DELETE)}
                onClick={() => {
                  openConfirmationModal({
                    title: t(keys.action.DELETE_VARIABLE, {
                      variable: t(keys.common.ASSESSMENT),
                    }),
                    description: t(keys.action.DELETE_CONFIRMATION, {
                      variable: inspection.template.title,
                    }),
                    variant: 'danger',
                    onSubmit: () => {
                      const route = navigationRoutes(
                        inspection.inspectionType,
                        workspace,
                      );
                      deleteInspection({
                        variables: { id: inspection.id },
                      }).then(() => {
                        navigate(route);
                      });
                      setTimeout(() => {
                        showToast({
                          title: inspection.template.title,
                          message: t(keys.action.DELETED, {
                            variable: inspection.template.title,
                          }),
                          variant: toastVariant.info,
                          time: toastLength.md,
                        });
                      }, 500);
                    },
                  });
                }}
              />
            </div>
          )}
      </div>
    </div>
  );
}
